import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import StatusBadge, { STATUS_LABEL } from "@/components/app/StatusBadge";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { X, Trash2, ArrowRight, AlertTriangle, Pencil, Download, Camera, MapPin, Scale, Shuffle, Layers, MessageSquare, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import PostToBinDialog from "@/pages/loads/PostToBinDialog";
import ReassignBinDialog from "@/pages/loads/ReassignBinDialog";
import LoadMessagesDrawer from "@/pages/loads/LoadMessagesDrawer";
import LoadMapDrawer from "@/pages/loads/LoadMapDrawer";

const SOIL_TYPES = ["Clean Fill", "Topsoil", "Clay", "Rock/Aggregate", "Contaminated Soil", "Sand", "Gravel"];
const EPA_COMMON = ["clean", "regulated", "hazardous", "polluted", "contaminated", "urban_fill", "virgin_material"];

function money(n) {
  return "$" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Loads() {
  const { user } = useAuth();
  const canReassign = user?.role === "admin" || user?.role === "compliance_officer";
  const canPostToBin = canReassign || user?.role === "dispatcher" || user?.role === "field_inspector";
  const [loads, setLoads] = useState([]);
  const [sites, setSites] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stockpiles, setStockpiles] = useState([]);
  const [containerTypes, setContainerTypes] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | load object
  const [posting, setPosting] = useState(null); // load being posted-to-bin
  const [reassigning, setReassigning] = useState(null); // load being reassigned
  const [messaging, setMessaging] = useState(null); // load whose thread is open
  const [mapping, setMapping] = useState(null); // load whose route is open
  const [unread, setUnread] = useState({}); // {load_id: count}
  const [filter, setFilter] = useState("all");

  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/loads/messages/unread-counts");
      setUnread(data || {});
    } catch {/* silent — non-critical */}
  }, []);

  const refresh = useCallback(async () => {
    const [l, s, t, d, p, sp, ct, mt] = await Promise.all([
      api.get("/loads"),
      api.get("/sites"),
      api.get("/trucks"),
      api.get("/drivers"),
      api.get("/projects"),
      api.get("/stockpiles"),
      api.get("/container-types"),
      api.get("/material-types"),
    ]);
    setLoads(l.data); setSites(s.data); setTrucks(t.data); setDrivers(d.data); setProjects(p.data); setStockpiles(sp.data);
    setContainerTypes(ct.data); setMaterialTypes(mt.data);
    refreshUnread();
  }, [refreshUnread]);
  useEffect(() => { refresh(); }, [refresh]);

  const pickupSites = sites.filter((s) => s.site_type === "construction");
  const dropoffSites = sites.filter((s) => s.site_type !== "construction");

  const [mineOnly, setMineOnly] = useState(false);
  const filtered = useMemo(() => {
    let out = filter === "all" ? loads : loads.filter((l) => l.status === filter);
    if (mineOnly && user?.id) {
      out = out.filter((l) => l.driver_user_id === user.id);
    }
    return out;
  }, [loads, filter, mineOnly, user?.id]);

  const driverUsers = useMemo(() => {
    // Users assigned to any current load — pulled via /users so we can render names.
    return null; // populated by driverNameById below via lookup
  }, []);
  const [driverNameById, setDriverNameById] = useState({});
  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/users").then((r) => {
        const map = {};
        (r.data || []).forEach((u) => { map[u.id] = u.name || u.email; });
        setDriverNameById(map);
      }).catch(() => {});
    }
  }, [user?.role]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/loads/${id}/status`, { status });
      toast.success(`Load ${STATUS_LABEL[status]}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this load?")) return;
    await api.delete(`/loads/${id}`);
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Loads"
        subtitle="Hauling jobs"
        actions={
          <button
            onClick={() => setEditing("new")}
            data-testid="new-load-button"
            className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5 transition-colors"
          >
            + New Load
          </button>
        }
      />

      <div className="p-8">
        {/* Filter Tabs */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white border border-gray-200 p-1 w-fit">
            {["all", "scheduled", "picked_up", "in_transit", "delivered"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                  filter === f ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {f === "all" ? "All" : STATUS_LABEL[f]}
              </button>
            ))}
          </div>
          {user?.role === "driver" ? null : (
            <button
              onClick={() => setMineOnly((v) => !v)}
              data-testid="filter-mine"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold border transition-colors ${
                mineOnly
                  ? "bg-orange-500 text-white border-orange-500"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              title="Show only loads assigned to me"
            >
              My Loads
            </button>
          )}
        </div>

        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="loads-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-2.5 label-caps">BOL #</th>
                <th className="text-left px-4 py-2.5 label-caps">Route</th>
                <th className="text-left px-4 py-2.5 label-caps">Material</th>
                <th className="text-right px-4 py-2.5 label-caps">Tons</th>
                <th className="text-right px-4 py-2.5 label-caps">Rate</th>
                <th className="text-left px-4 py-2.5 label-caps">Assigned To</th>
                <th className="text-left px-4 py-2.5 label-caps">Compliance</th>
                <th className="text-left px-4 py-2.5 label-caps">Status</th>
                <th className="text-right px-4 py-2.5 label-caps">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-12 text-center text-sm text-gray-500">No loads found.</td></tr>
              ) : filtered.map((l) => {
                const pickup = sites.find((s) => s.id === l.pickup_site_id);
                const drop = sites.find((s) => s.id === l.dropoff_site_id);
                const next = { scheduled: "picked_up", picked_up: "in_transit", in_transit: "delivered" }[l.status];
                return (
                  <tr key={l.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`load-row-${l.bol_number}`}>
                    <td className="px-4 py-3">
                      <Link to={`/bol/${l.id}`} className="font-mono text-xs text-gray-900 hover:text-orange-600" data-testid={`bol-link-${l.bol_number}`}>
                        {l.bol_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[140px]" title={pickup?.name}>{pickup?.name || "—"}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="truncate max-w-[140px]" title={drop?.name}>{drop?.name || "—"}</span>
                          {l.bin_number && (
                            <span
                              className="flex items-center gap-0.5 text-[10px] font-mono font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 flex-shrink-0"
                              title={`Posted ${l.posted_tons ?? l.weight_tons}t to bin ${l.bin_number}`}
                              data-testid={`bin-chip-${l.bol_number}`}
                            >
                              <Layers className="w-2.5 h-2.5" />
                              {l.bin_number}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      <div className="flex items-center gap-2">
                        {l.soil_type}
                        {l.hazardous && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{l.weight_tons}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-gray-700">{money(l.rate_per_ton)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700" data-testid={`assigned-${l.bol_number}`}>
                      {l.driver_user_id ? (
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                          <span className="truncate max-w-[140px]" title={driverNameById[l.driver_user_id] || "Assigned"}>
                            {driverNameById[l.driver_user_id] || (l.driver_user_id === user?.id ? "Me" : "Assigned")}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-gray-600">EPA {l.epa_classification}</span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {next && (
                          <button
                            onClick={() => updateStatus(l.id, next)}
                            data-testid={`advance-${l.bol_number}`}
                            className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold border border-gray-300 hover:bg-gray-900 hover:text-white transition-colors"
                          >
                            → {STATUS_LABEL[next]}
                          </button>
                        )}
                        {!["delivered", "cancelled", "rejected"].includes(l.status) && (
                          <button
                            onClick={() => setEditing(l)}
                            data-testid={`edit-load-${l.bol_number}`}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                            title="Edit manifest"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {l.status === "delivered" && canPostToBin && (
                          <button
                            onClick={() => setPosting(l)}
                            data-testid={`post-bin-${l.bol_number}`}
                            className="p-1.5 text-emerald-700 hover:bg-emerald-50 transition-colors"
                            title={l.bin_number ? `Re-post to bin (currently ${l.bin_number})` : "Post to bin"}
                          >
                            <Scale className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {l.status === "delivered" && l.bin_number && canReassign && (
                          <button
                            onClick={() => setReassigning(l)}
                            data-testid={`reassign-bin-${l.bol_number}`}
                            className="p-1.5 text-orange-700 hover:bg-orange-50 transition-colors"
                            title="Reassign bin (compliance)"
                          >
                            <Shuffle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => setMapping(l)}
                          data-testid={`map-load-${l.bol_number}`}
                          className="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                          title="View route on map"
                        >
                          <MapIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setMessaging(l)}
                          data-testid={`messages-load-${l.bol_number}`}
                          className="relative p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                          title={unread[l.id] ? `${unread[l.id]} unread message${unread[l.id] === 1 ? "" : "s"}` : "Discussion thread"}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          {unread[l.id] > 0 && (
                            <span
                              data-testid={`unread-badge-${l.bol_number}`}
                              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none"
                            >
                              {unread[l.id] > 9 ? "9+" : unread[l.id]}
                            </span>
                          )}
                        </button>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/loads/${l.id}/pdf`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`pdf-load-${l.bol_number}`}
                          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                          title="Download BOL PDF"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`delete-load-${l.bol_number}`}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <NewLoadSheet
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); refresh(); }}
          pickupSites={pickupSites}
          dropoffSites={dropoffSites}
          trucks={trucks}
          drivers={drivers}
          projects={projects}
          stockpiles={stockpiles}
          containerTypes={containerTypes}
          materialTypes={materialTypes}
        />
      )}
      {posting && (
        <PostToBinDialog
          load={posting}
          stockpiles={stockpiles.filter((sp) => sp.site_id === posting.dropoff_site_id)}
          onClose={() => setPosting(null)}
          onSaved={() => { setPosting(null); refresh(); }}
        />
      )}
      {reassigning && (
        <ReassignBinDialog
          load={reassigning}
          stockpiles={stockpiles.filter((sp) => sp.site_id === reassigning.dropoff_site_id && sp.id !== reassigning.stockpile_id)}
          onClose={() => setReassigning(null)}
          onSaved={() => { setReassigning(null); refresh(); }}
        />
      )}
      {messaging && (
        <LoadMessagesDrawer
          load={messaging}
          onClose={() => { setMessaging(null); refreshUnread(); }}
        />
      )}
      {mapping && (
        <LoadMapDrawer load={mapping} sites={sites} onClose={() => setMapping(null)} />
      )}
    </div>
  );
}

function NewLoadSheet({ initial, onClose, onCreated, pickupSites, dropoffSites, trucks, drivers, projects, stockpiles, containerTypes = [], materialTypes = [] }) {
  const isEdit = !!initial;
  const [driverUsers, setDriverUsers] = useState([]);
  React.useEffect(() => {
    api.get("/users").then((r) => setDriverUsers((r.data || []).filter((u) => u.role === "driver"))).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    project_id: initial?.project_id || "",
    pickup_site_id: initial?.pickup_site_id || "",
    dropoff_site_id: initial?.dropoff_site_id || "",
    truck_id: initial?.truck_id || "",
    driver_id: initial?.driver_id || "",
    driver_user_id: initial?.driver_user_id || "",
    soil_type: initial?.soil_type || "Clean Fill",
    volume_yards: initial?.volume_yards ?? 20,
    weight_tons: initial?.weight_tons ?? 15,
    epa_classification: initial?.epa_classification || "clean",
    stockpile_id: initial?.stockpile_id || "",
    dot_permit: initial?.dot_permit || "",
    hazardous: initial?.hazardous || false,
    rate_per_ton: initial?.rate_per_ton ?? 45,
    disposal_fee: initial?.disposal_fee ?? 0,
    notes: initial?.notes || "",
    pickup_lat: initial?.pickup_lat ?? null,
    pickup_lon: initial?.pickup_lon ?? null,
    neighborhood: initial?.neighborhood || null,
    neighborhood_other: initial?.neighborhood_other || "",
    soil_class: initial?.soil_class || null,
    soil_class_other: initial?.soil_class_other || "",
    petroleum_odor: initial?.petroleum_odor || null,
    visible_contamination: initial?.visible_contamination || [],
    container_type: initial?.container_type || "",
    material_description: initial?.material_description || "",
    generator_signer: initial?.generator_signer || "",
    facility_po_number: initial?.facility_po_number || "",
  });
  const [busy, setBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]); // File[] queued for after-create upload
  const [uploadedPhotos, setUploadedPhotos] = useState(initial?.photo_paths || []);

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, pickup_lat: pos.coords.latitude, pickup_lon: pos.coords.longitude }));
        toast.success(`GPS captured: ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        setGpsBusy(false);
      },
      (err) => { toast.error(`GPS: ${err.message}`); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const uploadPhoto = async (loadId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/loads/${loadId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let loadId = initial?.id;
      if (isEdit) {
        const { pickup_site_id, dropoff_site_id, project_id, pickup_lat, pickup_lon, ...editable } = form;
        const payload = { ...editable, truck_id: form.truck_id || null, driver_id: form.driver_id || null, driver_user_id: form.driver_user_id || null, stockpile_id: form.stockpile_id || null };
        await api.patch(`/loads/${initial.id}`, payload);
        toast.success(`Manifest updated to v${(initial.manifest_version || 1) + 1}`);
      } else {
        const payload = { ...form, project_id: form.project_id || null, truck_id: form.truck_id || null, driver_id: form.driver_id || null, driver_user_id: form.driver_user_id || null, stockpile_id: form.stockpile_id || null };
        const { data } = await api.post("/loads", payload);
        loadId = data.id;
      }
      // upload any queued photos
      for (const f of pendingPhotos) {
        try { await uploadPhoto(loadId, f); }
        catch (err) { toast.error(`Photo upload failed: ${err.response?.data?.detail || err.message}`); }
      }
      if (!isEdit && pendingPhotos.length > 0) toast.success(`${pendingPhotos.length} photo(s) attached`);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="new-load-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full overflow-y-auto border-l border-gray-200 shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="label-caps">{isEdit ? `Edit Manifest v${initial.manifest_version || 1}` : "New Load"}</div>
            <div className="font-display font-bold text-2xl tracking-tight">{isEdit ? initial.bol_number : "Create Haul"}</div>
          </div>
          <button onClick={onClose} data-testid="close-load-sheet" className="p-2 hover:bg-gray-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Project">
            <select value={form.project_id} onChange={(e) => set("project_id", e.target.value)} data-testid="project-select" className="input-sharp" disabled={isEdit}>
              <option value="">Unassigned</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </Field>
          <Field label="Pickup Site">
            <select required value={form.pickup_site_id} onChange={(e) => set("pickup_site_id", e.target.value)} data-testid="pickup-site-select" className="input-sharp" disabled={isEdit}>
              <option value="">Select…</option>
              {pickupSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Drop-off Site">
            <select required value={form.dropoff_site_id} onChange={(e) => set("dropoff_site_id", e.target.value)} data-testid="dropoff-site-select" className="input-sharp" disabled={isEdit}>
              <option value="">Select…</option>
              {dropoffSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Truck">
              <select value={form.truck_id} onChange={(e) => set("truck_id", e.target.value)} className="input-sharp" data-testid="truck-select">
                <option value="">Unassigned</option>
                {trucks.map((t) => <option key={t.id} value={t.id}>{t.truck_number} ({t.capacity_tons}t)</option>)}
              </select>
            </Field>
            <Field label="Driver">
              <select value={form.driver_id} onChange={(e) => set("driver_id", e.target.value)} className="input-sharp" data-testid="driver-select">
                <option value="">Unassigned</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Driver App Account · mobile scoping">
            <select value={form.driver_user_id} onChange={(e) => set("driver_user_id", e.target.value)} className="input-sharp" data-testid="driver-user-select">
              <option value="">— Not assigned (driver won't see this load in their app) —</option>
              {driverUsers.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
            </select>
          </Field>

          <Field label="Soil Type">
            <select value={form.soil_type} onChange={(e) => set("soil_type", e.target.value)} className="input-sharp" data-testid="soil-type-select">
              {SOIL_TYPES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Volume (yards³)">
              <input type="number" step="0.1" required value={form.volume_yards} onChange={(e) => set("volume_yards", parseFloat(e.target.value))} className="input-sharp" data-testid="volume-input" />
            </Field>
            <Field label="Weight (tons)">
              <input type="number" step="0.1" required value={form.weight_tons} onChange={(e) => set("weight_tons", parseFloat(e.target.value))} className="input-sharp" data-testid="weight-input" />
            </Field>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="label-caps mb-3">Federal Compliance</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="EPA / Class Tag">
                <input
                  list="epa-tags"
                  value={form.epa_classification}
                  onChange={(e) => set("epa_classification", e.target.value)}
                  className="input-sharp font-mono"
                  data-testid="epa-input"
                  placeholder="clean / polluted / …"
                />
                <datalist id="epa-tags">
                  {EPA_COMMON.map((s) => <option key={s} value={s} />)}
                </datalist>
              </Field>
              <Field label="DOT Permit #">
                <input value={form.dot_permit} onChange={(e) => set("dot_permit", e.target.value)} className="input-sharp font-mono" data-testid="dot-input" placeholder="DOT-XXXXX" />
              </Field>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
              <input type="checkbox" checked={form.hazardous} onChange={(e) => set("hazardous", e.target.checked)} data-testid="hazardous-checkbox" className="w-4 h-4 accent-red-500" />
              <span className="text-gray-800">Hazardous material (RCRA regulated)</span>
            </label>
            {form.dropoff_site_id && (
              <div className="mt-3">
                <Field label="Destination Bin (optional pre-assignment)">
                  <select
                    value={form.stockpile_id}
                    onChange={(e) => set("stockpile_id", e.target.value)}
                    className="input-sharp"
                    data-testid="destination-bin-select"
                  >
                    <option value="">Auto — match by classification at drop-off site</option>
                    {(stockpiles || []).filter((sp) => sp.site_id === form.dropoff_site_id).map((sp) => (
                      <option key={sp.id} value={sp.id}>
                        {sp.bin_number} · {sp.name} · {sp.classification} · {(sp.current_tons ?? 0).toFixed(1)}/{sp.capacity_tons}t
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="text-[11px] text-gray-500 mt-1">
                  Pick a specific bin, or leave on Auto to let the yard match by classification on delivery. Scale-house and compliance can adjust or reassign later.
                </div>
              </div>
            )}
          </div>

          {/* MDC Inspector - Field Observation */}
          <div className="border-t border-gray-200 pt-4">
            <div className="label-caps mb-3">MDC Inspector — Field Observation</div>
            <div className="space-y-3">
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-1">Neighborhood</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["residential","Residential"],["commercial","Commercial"],["other","Other"]].map(([k,l]) => (
                    <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" name="neighborhood" checked={form.neighborhood===k} onChange={() => set("neighborhood", k)} data-testid={`neighborhood-${k}`} className="w-3.5 h-3.5 accent-orange-500" />
                      {l}
                    </label>
                  ))}
                </div>
                {form.neighborhood === "other" && <input value={form.neighborhood_other || ""} onChange={(e) => set("neighborhood_other", e.target.value)} placeholder="Describe…" className="input-sharp mt-1.5" data-testid="neighborhood-other-input" />}
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-1">Soil Type</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["virgin_material","Virgin Material"],["urban_fill","Urban Fill"],["other","Other"]].map(([k,l]) => (
                    <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" name="soil_class" checked={form.soil_class===k} onChange={() => set("soil_class", k)} data-testid={`soil-class-${k}`} className="w-3.5 h-3.5 accent-orange-500" />
                      {l}
                    </label>
                  ))}
                </div>
                {form.soil_class === "other" && <input value={form.soil_class_other || ""} onChange={(e) => set("soil_class_other", e.target.value)} placeholder="Describe…" className="input-sharp mt-1.5" data-testid="soil-class-other-input" />}
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-1">Petroleum Odor</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["none","None"],["slight","Slight"],["strong","Strong"]].map(([k,l]) => (
                    <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" name="petroleum_odor" checked={form.petroleum_odor===k} onChange={() => set("petroleum_odor", k)} data-testid={`odor-${k}`} className="w-3.5 h-3.5 accent-orange-500" />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-1">Visible Contamination</div>
                <div className="grid grid-cols-3 gap-2">
                  {[["unknown_substance","Unknown Substance"],["staining","Staining"],["free_phase_oil","Free Phase Oil"]].map(([k,l]) => (
                    <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="checkbox" checked={(form.visible_contamination||[]).includes(k)} onChange={(e) => set("visible_contamination", e.target.checked ? [...(form.visible_contamination||[]), k] : (form.visible_contamination||[]).filter((x)=>x!==k))} data-testid={`contam-${k}`} className="w-3.5 h-3.5 accent-red-500" />
                      {l}
                    </label>
                  ))}
                </div>
                {(form.visible_contamination||[]).includes("free_phase_oil") && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 text-[11px] text-red-800 font-semibold">
                    ⚠ Notify Fuss &amp; O'Neill — Free Phase Oil detected
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Container Type">
                  <select value={form.container_type || ""} onChange={(e) => set("container_type", e.target.value)} className="input-sharp" data-testid="container-input">
                    <option value="">— select —</option>
                    {containerTypes.map((ct) => (
                      <option key={ct.id} value={ct.code}>{ct.code} · {ct.description}</option>
                    ))}
                    {form.container_type && !containerTypes.some((ct) => ct.code === form.container_type) && (
                      <option value={form.container_type}>{form.container_type} (legacy)</option>
                    )}
                  </select>
                </Field>
                <Field label="Material Description"><input value={form.material_description || ""} onChange={(e) => set("material_description", e.target.value)} className="input-sharp" data-testid="material-desc-input" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Inspector Name & Title"><input value={form.generator_signer || ""} onChange={(e) => set("generator_signer", e.target.value)} className="input-sharp" data-testid="inspector-signer-input" /></Field>
                <Field label="Facility PO #"><input value={form.facility_po_number || ""} onChange={(e) => set("facility_po_number", e.target.value)} className="input-sharp font-mono" data-testid="po-input" /></Field>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 grid grid-cols-2 gap-3">
            <Field label="Rate / ton ($)">
              <input type="number" step="0.01" value={form.rate_per_ton} onChange={(e) => set("rate_per_ton", parseFloat(e.target.value))} className="input-sharp" data-testid="rate-input" />
            </Field>
            <Field label="Disposal fee ($)">
              <input type="number" step="0.01" value={form.disposal_fee} onChange={(e) => set("disposal_fee", parseFloat(e.target.value))} className="input-sharp" data-testid="disposal-input" />
            </Field>
          </div>

          <Field label="Notes">
            <textarea rows="2" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="input-sharp" data-testid="notes-input" />
          </Field>

          {/* Field capture — GPS + photos (mobile-friendly) */}
          <div className="border-t border-gray-200 pt-4">
            <div className="label-caps mb-3">Field Capture</div>
            <div className="flex items-center gap-2 mb-3">
              <button type="button" onClick={captureGps} disabled={gpsBusy} data-testid="capture-gps-button" className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 hover:bg-gray-900 hover:text-white text-xs uppercase tracking-widest font-semibold disabled:opacity-50">
                <MapPin className="w-3.5 h-3.5" /> {gpsBusy ? "Getting GPS…" : (form.pickup_lat ? `GPS: ${form.pickup_lat.toFixed(4)}, ${form.pickup_lon.toFixed(4)}` : "Capture Pickup GPS")}
              </button>
            </div>
            <label className="flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-gray-400 text-gray-700 hover:bg-gray-50 cursor-pointer text-xs uppercase tracking-widest font-semibold" data-testid="photo-add-button">
              <Camera className="w-3.5 h-3.5" />
              Add Photo{isEdit ? " (uploads on save)" : "s"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                data-testid="photo-input"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (isEdit && files.length > 0) {
                    // Upload immediately in edit mode
                    (async () => {
                      for (const f of files) {
                        try {
                          const path = await uploadPhoto(initial.id, f);
                          setUploadedPhotos((arr) => [...arr, path]);
                          toast.success(`Uploaded ${f.name}`);
                        } catch (err) {
                          toast.error(`${f.name}: ${err.response?.data?.detail || err.message}`);
                        }
                      }
                    })();
                  } else {
                    setPendingPhotos((arr) => [...arr, ...files]);
                  }
                  e.target.value = "";
                }}
              />
            </label>
            {(pendingPhotos.length > 0 || uploadedPhotos.length > 0) && (
              <div className="mt-2 text-xs font-mono text-gray-700 space-y-1" data-testid="photo-list">
                {uploadedPhotos.map((p, i) => (
                  <div key={`u${i}`} className="flex items-center gap-2 text-emerald-700 truncate">
                    <Camera className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{p.split("/").pop()}</span>
                  </div>
                ))}
                {pendingPhotos.map((f, i) => (
                  <div key={`p${i}`} className="flex items-center justify-between gap-2 text-gray-600 truncate">
                    <span className="flex items-center gap-2 min-w-0"><Camera className="w-3 h-3 flex-shrink-0" /><span className="truncate">{f.name} · {(f.size / 1024).toFixed(0)}KB (queued)</span></span>
                    <button type="button" onClick={() => setPendingPhotos((arr) => arr.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={busy} data-testid="submit-load-button" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-50">
              {busy ? (isEdit ? "Updating…" : "Creating…") : (isEdit ? `Update to v${(initial.manifest_version || 1) + 1}` : "Create Load")}
            </button>
          </div>
        </form>
      </div>

      <style>{`.input-sharp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; background: white; } .input-sharp:focus { outline: none; border-color: #111827; box-shadow: 0 0 0 1px #111827; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-caps block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

